import { useMemo } from 'react';
// @mui
import { Box, Divider, SxProps } from '@mui/material';
import { PieChart } from '@mui/x-charts';
// hooks
import useChartSettings from 'src/hooks/useChartSettings';
// components
import { ChartPlaceholder } from 'src/components/chart/NoDataPlaceholder';
import { TooltipContainer, TooltipRow } from 'src/components/chart/ChartTooltip';
import SkeletonNoData from 'src/components/skeleton/SkeletonNoData';
//
import { readableStringFormatter } from 'src/utils/stringFormatter';
import { formatNumber } from 'src/utils/numberFormatter';
import { reorderArray } from 'src/utils/arrayFormatter';

const CustomItemTooltipContent = (props: any) => {
  const { itemData, series } = props;
  const currentItem = series?.data[itemData.dataIndex];

  return (
    <TooltipContainer>
      <TooltipRow label={currentItem?.tooltipLabel} currentItem={currentItem} showDot />
      <Divider sx={{ my: 1 }} />
      <TooltipRow
        label="Share:"
        value={`${series?.data[itemData.dataIndex]?.percentageOfTransactions}%`}
        currentItem={currentItem}
      />
      <TooltipRow
        label="Passengers:"
        value={series?.data[itemData.dataIndex]?.numberOfPassengers}
        currentItem={currentItem}
      />
      <TooltipRow
        label="Offset CO₂:"
        value={`${formatNumber(
          series?.data[itemData.dataIndex]?.co2AmountKgs?.value
        )} ${series?.data[itemData.dataIndex]?.co2AmountKgs?.unit?.toLowerCase()}`}
        currentItem={currentItem}
      />
    </TooltipContainer>
  );
};

interface Props {
  data: any[];
  isLoading: boolean;
  height?: number;
  sx?: SxProps;
}

const DistanceBreakdown: React.FC<Props> = ({ data, isLoading, sx }) => {
  const noDataText = 'See a breakdown of carbon offsets by flight distance.';

  const distanceDefinitions: { [key: string]: number[] } = useMemo(() => {
    return {
      SHORT: [0, 2000],
      MEDIUM: [2001, 4000],
      LONG: [4001, 10000],
      ULTRA_LONG: [10000],
    };
  }, []);

  const distanceBreakdown = useMemo(() => {
    const column_order = Object.keys(distanceDefinitions);

    if (data?.length > 0) {
      const result = data?.map((item) => {
        const definition = distanceDefinitions[item?.distanceCategory];
        const label = definition?.length === 2
          ? `${definition[0].toLocaleString()} - ${definition[1].toLocaleString()} km`
          : `Over ${definition[0].toLocaleString()} km`;
        const distanceCategory = readableStringFormatter(item?.distanceCategory);

        return {
          ...item,
          label: distanceCategory,
          tooltipLabel: `${distanceCategory}: ${label}`,
          value: item?.numberOfTransactions,
        };
      });

      const sortedArray = reorderArray(column_order, result, 'distanceCategory');
      return sortedArray;
    } else {
      return [];
    }
  }, [data, distanceDefinitions]);

  const { pieChartSetting } = useChartSettings(distanceBreakdown);

  return (
      <SkeletonNoData
        isLoading={isLoading}
        hasData={distanceBreakdown?.length > 0}
        text={noDataText}
        Placeholder={ChartPlaceholder}
        height={260}
      >
        <Box sx={{ width: '100%', height: '100%', ...sx }}>
          <PieChart
            series={[{ data: distanceBreakdown, type: 'pie' }]}
            tooltip={{
              trigger: 'item',
              itemContent: CustomItemTooltipContent,
            }}
            {...pieChartSetting}
            height={pieChartSetting?.height}
          />
        </Box>
      </SkeletonNoData>
  );
};

export default DistanceBreakdown;
